/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool, func, number, object, string,
} from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Link } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@material-ui/core/IconButton';

import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useRecentlySearched from '../../../../helpers/useRecentlySearched';
import RecentIcon from '../../../BrandTheme/Icons/RecentIcon';
import SearchClearIcon from '../../../BrandTheme/Icons/SearchClearIcon';
import SearchTypeAheadPopularSearches from './SearchTypeAheadPopularSearches';
import { getCurrentPageType } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const useStyles = makeStyles(() => ({
    desktopRoot: (props) => ({
        position: 'absolute',
        zIndex: '25',
        background: '#fff',
        top: '46px',
        left: 0,
        width: props.inputWidth ? `${props.inputWidth}px` : '100%',
        borderBottomRightRadius: '5px',
        borderBottomLeftRadius: '5px',
        paddingBottom: '6px',
        boxShadow: '0px 4px 4px 0px #00000040',
        maxHeight: 'calc(100vh - 92px)',
        overflowY: 'auto',
    }),
    phraseSuggestions: {
        listStyle: 'none',
        padding: '0px',
        margin: '0px',
        '& a': {
            display: 'block',
            flex: 1,
            padding: '16px',
            textDecoration: 'none',
            color: '#000000',
            fontSize: '14px',
            letterSpacing: '0.5px',
            lineHeight: '19px',
            alignItems: 'center',
            fontWeight: '400',
            '& span': {
                fontWeight: 'normal',
            },
            '& svg': {
                marginRight: '8px',
            },
        },
    },
    recentlySearchedListItem: {
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            backgroundColor: '#F5F5F5',
            borderRadius: '3px',
        },
    },
    selectedSuggestion: {
        backgroundColor: '#F5F5F5',
        borderRadius: '6px',
    },
    border: {
        margin: '0px 16px',
        border: 'none',
        borderBottom: '1px solid #E9E9E9',
    },
    searchMenuTitle: {
        fontSize: '14px',
        fontWeight: 800,
        padding: '16px 16px 8px',
    },
    clearSearchButton: {
        marginRight: '16px !important',
        padding: '6px !important',
        '&:hover': {
            backgroundColor: 'unset !important',
        },
    },
    recentlySearchedLinkText: {
        display: 'flex',
        alignItems: 'center',
    },
}));

const recentlySearchedList = (
    classes,
    phrases,
    handleClick,
    concatSearchTerm,
    cleanTerm,
    recentlySearchedIndex,
    recentlySearchedDataRef,
    isDesktop,
) => {
    const recentlySearched = [];
    let response = null;
    if (phrases?.length) {
        phrases.slice(0, 3).forEach(({ searchTerm, value }) => {
            const data = {};
            const searchValue = value.toLowerCase();
            data.name = searchTerm;
            data.value = value;
            data.inputString = searchValue;
            data.link = `/searchterm/${concatSearchTerm(cleanTerm(searchValue)?.replace(/\s+/g, '+'), value)}`; // replacing spaces with +
            recentlySearched.push(data);
        });
    }

    // Update the ref with current suggestions data
    if (recentlySearchedDataRef) {
        // eslint-disable-next-line no-param-reassign
        recentlySearchedDataRef.current = recentlySearched;
    }

    if (recentlySearched.length) {
        response = recentlySearched.map((data, index) => (
            <React.Fragment key={data.link}>
                <div className={classes.recentlySearchedListItem}>
                    <Link
                        id={`recently-searched-${index}`}
                        className={recentlySearchedIndex === index ? classes.selectedSuggestion : ''}
                        aria-selected={recentlySearchedIndex === index ? 'true' : 'false'}
                        role="option"
                        aria-label={`Recently searched ${index + 1} of ${recentlySearched.length}: ${data.inputString}`}
                        tabIndex="-1"
                        to={data.link}
                        onClick={() => handleClick(data)}
                    >
                        <div aria-hidden="true" className={classes.recentlySearchedLinkText}>
                            <RecentIcon />
                            {ReactHtmlParser(data.name)}
                        </div>
                    </Link>
                    <IconButton
                        onClick={() => handleClick(data, true)}
                        role="button"
                        aria-label={`Remove recently searched item ${data.inputString} from list`}
                        className={classes.clearSearchButton}
                        size="small"
                        title="Clear"
                    >
                        <SearchClearIcon fillColor="#2F363D" />
                    </IconButton>
                </div>
                {!(isDesktop && index === recentlySearched.length - 1) && <hr className={classes.border} />}
            </React.Fragment>
        ));
    }
    return response;
};

const SearchTypeAheadSuggestions = ({
    isDesktop,
    setSearchValue,
    cleanTerm,
    concatSearchTerm,
    inputWidth,
    setShowSuggestions,
    setShowTypeAheadSuggestions,
    recentlySearchedIndex,
    recentlySearchedDataRef,
    handleMobileClose,
    trackExperiment,
}) => {
    const classes = useStyles({ inputWidth: isDesktop ? inputWidth : undefined });
    const dispatch = useDispatch();
    const typeAheadRecentlySearched = useSelector(getFeatureFlag('is-search-ui-type-ahead-individual'))?.recentlySearched;
    const typeAheadPopularSearches = useSelector(getFeatureFlag('is-search-ui-type-ahead-individual'))?.popularSearches;

    const pageType = useSelector(getCurrentPageType);

    const { recentlySearched, removeRecentlySearched, addRecentlySearched } = useRecentlySearched();

    const handleRecentSearchClick = (data, remove = false) => {
        if (remove) {
            removeRecentlySearched(data.value);
            return;
        }
        setSearchValue(data.inputString);
        addRecentlySearched({ searchTerm: data.name, value: data.value });
        dispatch(trackEvent({
            eventCategory: 'Site Search',
            eventAction: data.inputString,
            eventLabel: 'RECENTLY SEARCHED | <<pageType>>',
            searchVariety: 'Recently Searched',
            searchPhrase: data.inputString,
            eventName: 'search',

            search_type: 'recently_searched',
            search_term: data?.inputString?.toLowerCase?.(),
            unique_search_term: data.inputString,
        }));
        setShowSuggestions(false);
        setShowTypeAheadSuggestions(false);
        handleMobileClose();
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Search',
            label: 'recentlySearched',
        }));
        trackExperiment('recentlySearched', 'recentlySearched');
    };

    if  (typeAheadPopularSearches || (typeAheadRecentlySearched && recentlySearched?.length > 0)) {
        return (
            <div
                className={isDesktop ? `${classes.desktopRoot}` : ''}
            >
                {typeAheadRecentlySearched && recentlySearched?.length > 0 && (
                    <ul
                        className={`${classes.phraseSuggestions} ${isDesktop ? classes.phraseSuggestionsDesktop : ''}`}
                        id="suggestion"
                        role="listbox"
                        aria-label="Recently searched list"
                    >
                        <div className={classes.searchMenuTitle}>Your recent searches</div>
                        {recentlySearchedList(
                            classes,
                            recentlySearched.slice(0, 3),
                            handleRecentSearchClick,
                            concatSearchTerm,
                            cleanTerm,
                            recentlySearchedIndex,
                            recentlySearchedDataRef,
                            isDesktop && !typeAheadPopularSearches,
                        )}
                    </ul>
                )}
                {typeAheadPopularSearches && (
                    <SearchTypeAheadPopularSearches
                        setSearchValue={setSearchValue}
                        setShowSuggestions={setShowSuggestions}
                        setShowTypeAheadSuggestions={setShowTypeAheadSuggestions}
                        handleMobileClose={handleMobileClose}
                        concatSearchTerm={concatSearchTerm}
                        cleanTerm={cleanTerm}
                        trackExperiment={trackExperiment}
                    />
                )}
            </div>
        );
    }
    return null;
};

SearchTypeAheadSuggestions.propTypes = {
    setSearchValue: func,
    concatSearchTerm: func,
    cleanTerm: func,
    isDesktop: bool,
    inputWidth: number,
    setShowSuggestions: func,
    setShowTypeAheadSuggestions: func,
    recentlySearchedIndex: number,
    recentlySearchedDataRef: object,
    handleMobileClose: func,
    trackExperiment: func,
};

SearchTypeAheadSuggestions.defaultProps = {
    setSearchValue: () => {},
    concatSearchTerm: () => {},
    cleanTerm: () => {},
    isDesktop: true,
    inputWidth: undefined,
    setShowSuggestions: () => {},
    setShowTypeAheadSuggestions: () => {},
    recentlySearchedIndex: -1,
    recentlySearchedDataRef: null,
    handleMobileClose: () => {},
    trackExperiment: () => {},
};

export default SearchTypeAheadSuggestions;
